/**
 * Sprint 12+M5.17-o (22.07.2026) — Cookie-Banner Mini-Redesign (Russell-Frame).
 *
 * Feedback-Sprint-Ziele:
 *   1. Von Modal (~400px) auf Mini-Banner (~60-80px) reduzieren
 *   2. Ein primaerer CTA "Passt" + Text-Link "Einstellungen" (Decision-Fatigue-Fix)
 *   3. Positiv gerahmter Copy ohne Verneinungen ("nicht random Werbung" raus)
 *   4. Detail-Steuerung via Bottom-Sheet mit Toggle-Schaltern
 *
 * DSGVO-Compliance:
 *   - Wahlfreiheit explizit (User kann via "Einstellungen" ablehnen)
 *   - Notwendig getrennt + erklaert
 *   - Widerruf jederzeit (aktuell via localStorage-Clear, spaeter
 *     via /cookie-einstellungen-Route)
 *   - Datenschutzerklaerung verlinkt
 *   - Granulare Steuerung im Bottom-Sheet moeglich
 *
 * States:
 *   "accepted"       → beide Kategorien (Notwendig + Personalisierung)
 *   "necessary_only" → nur Notwendig
 *   null             → noch nicht gefragt → Mini-Banner zeigen
 *
 * Trigger:
 *   Bei "accepted" wird `window.__f4s_loadMetaPixel()` aus index.html
 *   aufgerufen — laedt Meta Pixel + feuert erstes PageView.
 */
import React, { useEffect, useState } from "react";

const CONSENT_KEY = "f4s_consent";
const CONSENT_ACCEPTED = "accepted";
const CONSENT_NECESSARY = "necessary_only";

function readConsent() {
  try {
    return localStorage.getItem(CONSENT_KEY) || null;
  } catch {
    return null;
  }
}

function writeConsent(value) {
  try {
    localStorage.setItem(CONSENT_KEY, value);
  } catch {
    /* localStorage disabled — Banner erscheint dann bei jedem Reload */
  }
}

function loadPixelIfConsented() {
  try {
    if (typeof window.__f4s_loadMetaPixel === "function") {
      window.__f4s_loadMetaPixel();
    }
    // Sprint 12+M5.21 (24.07.2026) — GA4 Consent-Update analog Meta-Pixel.
    // Bei "Alle akzeptieren" wird auch das Analytics-Cookie-Storage
    // freigeschaltet und gtag.js lazy geladen.
    if (typeof window.__f4s_grantAnalyticsConsent === "function") {
      window.__f4s_grantAnalyticsConsent();
    }
  } catch {
    /* non-fatal */
  }
}

// -------------- Detail-Steuerung (Bottom-Sheet mit Toggle) --------------
function DetailSheet({ open, onSave, onClose }) {
  // Sprint 12+M5.22 P0 (24.07.2026) — DSGVO-Fix: Toggle default AUS
  // (EuGH "Planet49" C-673/17 verbietet vor-aktivierte Consents).
  const [personalizationOn, setPersonalizationOn] = useState(false);

  useEffect(() => {
    if (open) {
      // Body-Scroll bei geoeffnetem Sheet blockieren
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [open]);

  if (!open) return null;

  function handleSave() {
    onSave(personalizationOn ? CONSENT_ACCEPTED : CONSENT_NECESSARY);
  }

  return (
    <div
      className="fixed inset-0 z-[10000]"
      role="dialog"
      aria-modal="true"
      aria-label="Cookie-Einstellungen — Detail"
      data-testid="cookie-detail-sheet"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-[2px]"
        onClick={onClose}
        data-testid="cookie-detail-backdrop"
      />
      {/* Sheet — Bottom on Mobile, centered card on md+ */}
      <div
        className="absolute inset-x-0 bottom-0 md:inset-0 md:flex md:items-center md:justify-center md:p-4"
      >
        <div className="mx-auto w-full md:max-w-md bg-white rounded-t-2xl md:rounded-2xl shadow-2xl p-5 sm:p-6 max-h-[85vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base sm:text-lg font-extrabold text-slate-900">
              Deine Cookie-Einstellungen
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="w-8 h-8 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 flex items-center justify-center text-xl"
              aria-label="Schließen"
              data-testid="cookie-detail-close"
            >
              ×
            </button>
          </div>

          {/* Notwendig — immer aktiv */}
          <div
            className="rounded-xl border border-slate-200 p-4 mb-3 bg-slate-50"
            data-testid="cookie-detail-necessary-row"
          >
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="font-semibold text-slate-900 text-sm">Notwendig</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  Damit die Seite funktioniert (Login, Sicherheit).
                </div>
              </div>
              <div className="flex-shrink-0 flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">
                  Immer aktiv
                </span>
                <div className="w-10 h-6 rounded-full bg-emerald-500 flex items-center justify-end px-0.5 opacity-60">
                  <div className="w-5 h-5 rounded-full bg-white shadow" />
                </div>
              </div>
            </div>
          </div>

          {/* Passend zu dir — Toggle */}
          <div
            className="rounded-xl border border-slate-200 p-4 mb-5"
            data-testid="cookie-detail-personalization-row"
          >
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="font-semibold text-slate-900 text-sm">Passend zu dir</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  Damit wir dir Inhalte passend zu deinem Flex4Success-Muster zeigen.
                </div>
              </div>
              <button
                type="button"
                onClick={() => setPersonalizationOn((v) => !v)}
                aria-pressed={personalizationOn}
                aria-label={`Personalisierung ${personalizationOn ? "aktiv" : "inaktiv"}`}
                data-testid="cookie-detail-personalization-toggle"
                className={`flex-shrink-0 w-11 h-6 rounded-full transition-colors flex items-center px-0.5 ${
                  personalizationOn
                    ? "bg-[#4563BB] justify-end"
                    : "bg-slate-300 justify-start"
                }`}
              >
                <span className="w-5 h-5 rounded-full bg-white shadow transition-transform" />
              </button>
            </div>
            {/* Sprint 12+M5.22 P0 (24.07.2026) — DSGVO-Fix:
                Anbieter/Empfaenger benannt (Art. 13 DSGVO), Drittland-Hinweis
                fuer USA (Schrems-II / EU-US-Data-Privacy-Framework). */}
            <div className="mt-3 text-[11px] text-slate-500 leading-relaxed">
              <span className="font-semibold text-slate-700">Anbieter:</span>{" "}
              Google Analytics 4 (Google Ireland Ltd.), Meta Pixel (Meta Platforms Ireland Ltd.),
              PostHog (PostHog Inc., USA), Vimeo (Vimeo Inc., USA).{" "}
              <span className="font-semibold text-slate-700">Drittland:</span>{" "}
              Übermittlung in die USA auf Basis des EU-US-Data-Privacy-Framework.
            </div>
          </div>

          {/* Buttons */}
          <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3">
            <button
              type="button"
              onClick={() => onSave(CONSENT_NECESSARY)}
              className="flex-1 px-4 py-2.5 rounded-lg text-sm font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 transition-colors border border-slate-200"
              data-testid="cookie-detail-necessary-only"
            >
              Standard reicht
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="flex-1 px-4 py-2.5 rounded-lg text-sm font-semibold text-white bg-[#4563BB] hover:bg-[#3a54a3] transition-colors shadow-sm"
              data-testid="cookie-detail-save"
            >
              Speichern
            </button>
          </div>

          <p className="mt-4 text-[11px] text-slate-500 leading-relaxed">
            Details in unserer{" "}
            <a
              href="/datenschutz"
              className="underline text-[#4563BB] hover:text-[#3a54a3]"
            >
              Datenschutzerklärung
            </a>
            . Du kannst deine Wahl jederzeit ändern.
          </p>
        </div>
      </div>
    </div>
  );
}

// -------------- Mini-Banner (Bottom-fixed, ~60-80px) --------------
export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);

  useEffect(() => {
    // Sprint M5.23 (24.07.2026) — Cookie-Banner unterdruecken auf dedizierten
    // Landing-Pages (/lp/*). Dort werden keine Tracker geladen, deshalb ist
    // kein Consent-Banner rechtlich erforderlich. Consent-Aufforderung kommt
    // erst nach Magic-Link-Klick im Cockpit.
    if (typeof window !== "undefined" && window.location.pathname.startsWith("/lp/")) {
      return;
    }
    const existing = readConsent();
    if (!existing) {
      const t = setTimeout(() => setVisible(true), 700);
      return () => clearTimeout(t);
    }
  }, []);

  function handleAccept() {
    writeConsent(CONSENT_ACCEPTED);
    loadPixelIfConsented();
    setVisible(false);
    setSheetOpen(false);
  }

  function handleOpenDetails() {
    setSheetOpen(true);
  }

  function handleSheetSave(value) {
    writeConsent(value);
    if (value === CONSENT_ACCEPTED) {
      loadPixelIfConsented();
    }
    setSheetOpen(false);
    setVisible(false);
  }

  if (!visible && !sheetOpen) return null;

  return (
    <>
      {visible && (
        <div
          role="region"
          aria-label="Cookie-Hinweis"
          className="fixed bottom-0 inset-x-0 z-[9999] pointer-events-none"
          data-testid="cookie-banner"
        >
          <div className="mx-auto max-w-3xl m-2 sm:m-3 pointer-events-auto">
            <div
              className="rounded-xl shadow-xl border border-slate-200/70 bg-white/95 backdrop-blur px-4 py-3 sm:px-5 sm:py-3.5 flex flex-col sm:flex-row sm:items-center gap-3"
              data-testid="cookie-banner-inner"
            >
              <div
                className="flex-1 text-sm text-slate-800 leading-snug"
                data-testid="cookie-banner-copy"
              >
                <span className="mr-1.5" aria-hidden="true">🍪</span>
                Wir nutzen Cookies, damit dein Erlebnis zu dir passt.{" "}
                <a
                  href="/datenschutz"
                  className="underline underline-offset-2 text-slate-600 hover:text-slate-900"
                  data-testid="cookie-banner-privacy-link"
                >
                  Datenschutz
                </a>
              </div>
              {/* Sprint 12+M5.22 P0 (24.07.2026) — DSGVO-Fix:
                  BGH/DSK 2023 verlangt, dass "Ablehnen" gleich prominent wie
                  "Akzeptieren" auf dem First-Layer sichtbar ist. Bisher war nur
                  "Passt" prominent, "Einstellungen" nur als Text-Link, "Ablehnen"
                  gar nicht direkt erreichbar — jetzt drei gleichrangige Buttons. */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-2.5 justify-end sm:justify-start flex-shrink-0">
                <button
                  type="button"
                  onClick={() => {
                    writeConsent(CONSENT_NECESSARY);
                    setVisible(false);
                    setSheetOpen(false);
                  }}
                  className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-200 transition-colors"
                  data-testid="cookie-banner-reject"
                >
                  Nur Notwendig
                </button>
                <button
                  type="button"
                  onClick={handleOpenDetails}
                  className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-700 bg-white hover:bg-slate-50 border border-slate-200 transition-colors"
                  data-testid="cookie-banner-settings"
                >
                  Einstellungen
                </button>
                <button
                  type="button"
                  onClick={handleAccept}
                  className="px-5 py-2 rounded-lg text-sm font-semibold text-white bg-[#4563BB] hover:bg-[#3a54a3] shadow-sm transition-colors"
                  data-testid="cookie-banner-accept"
                >
                  Alle akzeptieren
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <DetailSheet
        open={sheetOpen}
        onSave={handleSheetSave}
        onClose={() => setSheetOpen(false)}
      />
    </>
  );
}
